import { alpha } from '@mui/material/styles';

// Helper
function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// Fallbacks — used when API doesn't provide
const DEFAULT_COLORS = {
  primary: {
    lighter: '#C8FACD',
    light: '#5BE584',
    main: '#00AB55',
    dark: '#007B55',
    darker: '#005249',
    contrastText: '#fff',
    gradient: "#a8c738"
  },
  secondary: {
    lighter: '#D6E4FF',
    light: '#84A9FF',
    main: '#3366FF',
    dark: '#1939B7',
    darker: '#091A7A',
    contrastText: '#fff',
  },
  info: {
    lighter: '#D0F2FF',
    light: '#74CAFF',
    main: '#1890FF',
    dark: '#0C53B7',
    darker: '#04297A',
    contrastText: '#fff',
  },
  success: {
    lighter: '#E9FCD4',
    light: '#AAF27F',
    main: '#54D62C',
    dark: '#229A16',
    darker: '#08660D',
    contrastText: '#212B36',
  },
  warning: {
    lighter: '#FFF7CD',
    light: '#FFE16A',
    main: '#FFC107',
    dark: '#B78103',
    darker: '#7A4F01',
    contrastText: '#212B36',
  },
  error: {
    lighter: '#FFE7D9',
    light: '#FFA48D',
    main: '#FF4842',
    dark: '#B72136',
    darker: '#7A0C2E',
    contrastText: '#fff',
  },
};

// Static GREY remains the same
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

// Store dynamic colors
let dynamicColors = null;

// Use dynamic if available, fallback to defaults
const getColor = (key) => (dynamicColors?.[key] || DEFAULT_COLORS[key]);

const GRADIENTS = {
  primary: createGradient(getColor('primary').light, getColor('primary').main),
  info: createGradient(getColor('info').light, getColor('info').main),
  success: createGradient(getColor('success').light, getColor('success').main),
  warning: createGradient(getColor('warning').light, getColor('warning').main),
  error: createGradient(getColor('error').light, getColor('error').main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

// Shared COMMON based on current color set
const getCOMMON = () => {
  const primary = getColor('primary');
  const secondary = getColor('secondary');
  const info = getColor('info');
  const success = getColor('success');
  const warning = getColor('warning');
  const error = getColor('error');

  return {
    common: { black: '#000', white: '#fff', offWhite: '#FAF9F6' },
    primary,
    secondary,
    info,
    success,
    warning,
    error,
    grey: GREY,
    gradients: GRADIENTS,
    chart: CHART_COLORS,
    divider: GREY[500_24],
    action: {
      hover: GREY[500_8],
      selected: GREY[500_16],
      disabled: GREY[500_80],
      disabledBackground: GREY[500_24],
      focus: GREY[500_24],
      hoverOpacity: 0.08,
      disabledOpacity: 0.48,
    },
  };
};

function createPalette() {
  const COMMON = getCOMMON();

  return {
    light: {
      ...COMMON,
      mode: 'light',
      text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
      background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
      action: { active: GREY[600], ...COMMON.action },
    },
    dark: {
      ...COMMON,
      mode: 'dark',
      text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
      background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
      action: { active: GREY[500], ...COMMON.action },
    }
  };
}

let palette = createPalette();

export function getPalette() {
  return palette;
}
// Function to set theme colors dynamically
export function setPaletteFromAPI(apiColors) {
  dynamicColors = {
    primary: { ...DEFAULT_COLORS.primary, ...(apiColors.primary || {}) },
    secondary: { ...DEFAULT_COLORS.secondary, ...(apiColors.secondary || {}) },
    info: { ...DEFAULT_COLORS.info, ...(apiColors.info || {}) },
    success: { ...DEFAULT_COLORS.success, ...(apiColors.success || {}) },
    warning: { ...DEFAULT_COLORS.warning, ...(apiColors.warning || {}) },
    error: { ...DEFAULT_COLORS.error, ...(apiColors.error || {}) },
  };

  palette = createPalette();

}

export default palette;
